"use client";

import { easeInOutQuad, flyToBounds, panToShowPopup } from "@pex/maps";
import { useIsMobile } from "@pex/ui-web/hooks/use-mobile";
import mapboxgl from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  PinDetailCard,
  PinDetailSheet,
} from "@/components/shared/pin-detail-card";
import type { DiscoveryItem } from "@/lib/queries/discovery";

// CSS is imported globally in app/globals.css

const FALLBACK: [number, number] = [25.0, -28.5]; // Southern Africa [lng, lat]
const FALLBACK_ZOOM = 5;

// Estimated rendered height of PinDetailCard (w-80, with header + content + footer).
// Used by panToShowPopup to ensure the popup fits above the pin.
const POPUP_HEIGHT = 400;

const PIN_COLOR_GROUP = "#111111";
const PIN_COLOR_DEFAULT = "#C5412A";

function getProgrammePinColor(code: string): string {
  const style = getComputedStyle(document.documentElement);
  const varMap: Record<string, string> = {
    ers: "--pin-ers",
    resound: "--pin-resound",
    vantage: "--pin-vantage",
    thrive: "--pin-thrive",
  };
  const cssVar = varMap[code];
  if (!cssVar) {
    return PIN_COLOR_DEFAULT;
  }
  return style.getPropertyValue(cssVar).trim() || PIN_COLOR_DEFAULT;
}

function pinColor(item: DiscoveryItem): string {
  if (item.kind === "group") {
    return PIN_COLOR_GROUP;
  }
  const code = item.data.definition?.code?.toLowerCase() ?? "";
  return getProgrammePinColor(code);
}

const PIN_PATH =
  "M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22S28 24.5 28 14C28 6.268 21.732 0 14 0z";

function pinSvg(color: string): string {
  return `<svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${PIN_PATH}" fill="${color}"/><circle cx="14" cy="14" r="6" fill="white" opacity="0.95"/></svg>`;
}

function splitPinSvg(colors: string[], uid: number): string {
  const n = Math.min(colors.length, 3);
  const sliceW = 28 / n;
  const defs = Array.from(
    { length: n },
    (_, i) =>
      `<clipPath id="sp${uid}i${i}"><rect x="${i * sliceW}" y="0" width="${sliceW}" height="36"/></clipPath>`
  ).join("");
  const fills = Array.from(
    { length: n },
    (_, i) =>
      `<path d="${PIN_PATH}" fill="${colors[i]}" clip-path="url(#sp${uid}i${i})"/>`
  ).join("");
  return `<svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg"><defs>${defs}</defs>${fills}<circle cx="14" cy="14" r="6" fill="white" opacity="0.95"/></svg>`;
}

function locationKey(item: DiscoveryItem): string {
  const lat = Math.round((item.data.latitude as number) * 10_000) / 10_000;
  const lng = Math.round((item.data.longitude as number) * 10_000) / 10_000;
  return `${lat},${lng}`;
}

export interface MapHandle {
  fitBounds(bbox: [number, number, number, number], onEnd?: () => void): void;
  flyTo(lngLat: [number, number], zoom?: number, onEnd?: () => void): void;
  getBounds(): mapboxgl.LngLatBounds | undefined;
  getCenter(): [number, number] | undefined;
  openPopup(item: DiscoveryItem): void;
  zoomIn(): void;
  zoomOut(): void;
}

interface Props {
  /** When true, render one pin per item rather than grouping co-located items. */
  filtersActive?: boolean;
  items: DiscoveryItem[];
  /** Called when the user starts a manual pan/drag — parent uses this to clear stale banners. */
  onUserInteraction?: () => void;
  ref?: React.Ref<MapHandle | null>;
}

function markerPin(
  ariaLabel: string,
  color: string,
  uid?: number,
  extraColors?: string[]
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", ariaLabel);
  btn.style.cssText =
    "width:28px;height:36px;cursor:pointer;padding:0;background:none;border:none;display:block;";

  const inner = document.createElement("div");
  inner.style.cssText =
    "width:28px;height:36px;transition:transform 0.12s ease;transform-origin:bottom center;";
  inner.innerHTML =
    extraColors && extraColors.length > 0 && uid !== undefined
      ? splitPinSvg([color, ...extraColors], uid)
      : pinSvg(color);
  btn.appendChild(inner);

  btn.addEventListener("mouseenter", () => {
    inner.style.transform = "scale(1.25)";
  });
  btn.addEventListener("mouseleave", () => {
    inner.style.transform = "scale(1)";
  });

  return btn;
}

function syncExternalRef(
  externalRef: React.Ref<MapHandle | null> | undefined,
  value: MapHandle | null
) {
  if (!externalRef) {
    return;
  }
  if (typeof externalRef === "function") {
    externalRef(value);
  } else {
    (externalRef as React.MutableRefObject<MapHandle | null>).current = value;
  }
}

interface PopupMount {
  el: HTMLDivElement;
  items: DiscoveryItem[];
}

export function MapWithPins({
  filtersActive = false,
  items,
  onUserInteraction,
  ref: externalRef,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Monotonically-increasing generation counter — prevents a stale "close"
  // listener from clearing a newly opened popup when pins are clicked rapidly.
  const popupGenRef = useRef(0);
  // Tracks flyTo generation so a user-interrupted pan doesn't trigger onEnd.
  const flyToGenRef = useRef(0);
  const [mapReady, setMapReady] = useState(false);
  const [popupMount, setPopupMount] = useState<PopupMount | null>(null);
  // Mobile shows pin details in a bottom sheet instead of a Mapbox popup.
  const isMobile = useIsMobile();
  const [sheetItems, setSheetItems] = useState<DiscoveryItem[] | null>(null);

  // Close the bottom sheet if the viewport widens past the mobile breakpoint
  // (e.g. landscape rotation on a tablet) — the sheet is mobile-only.
  useEffect(() => {
    if (!isMobile) {
      setSheetItems(null);
    }
  }, [isMobile]);

  // Stable ref to the popup-opening function so the MapHandle closure never goes stale
  const openPopupFnRef = useRef<((items: DiscoveryItem[]) => void) | null>(
    null
  );

  // Keep onUserInteraction in a ref so the map init effect can read it without
  // needing onUserInteraction as a dependency (avoiding map re-init on prop change).
  const onUserInteractionRef = useRef(onUserInteraction);
  onUserInteractionRef.current = onUserInteraction;

  // Tracks whether the initial flyToBounds has already fired.
  // After first load we leave the viewport alone so filter-pill changes don't
  // yank the map away from wherever the user is currently looking.
  const hasInitialFitRef = useRef(false);

  const inPersonItems = useMemo(
    () => items.filter((i) => i.data.format !== "online"),
    [items]
  );
  const geoItems = useMemo(
    () =>
      inPersonItems.filter(
        (i) => i.data.latitude != null && i.data.longitude != null
      ),
    [inPersonItems]
  );
  const hiddenCount = inPersonItems.length - geoItems.length;

  if (hiddenCount > 0) {
    console.warn(
      `[Finder/map-with-pins] ${hiddenCount} item(s) missing lat/lng and are not shown on the map.`
    );
  }

  // Initialise map — gated on container having non-zero size
  // biome-ignore lint/correctness/useExhaustiveDependencies: externalRef is a stable ref object — intentionally excluded
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let map: mapboxgl.Map | null = null;

    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect || rect.width === 0 || rect.height === 0) {
        return;
      }

      if (map) {
        map.resize();
        return;
      }

      mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? "";

      map = new mapboxgl.Map({
        container,
        style: "mapbox://styles/mapbox/streets-v12",
        center: FALLBACK,
        zoom: FALLBACK_ZOOM,
        projection: "mercator",
        attributionControl: false,
      });

      map.addControl(
        new mapboxgl.AttributionControl({ compact: true }),
        "bottom-right"
      );

      map.on("load", () => {
        if (!map) {
          return;
        }
        mapRef.current = map;
        setMapReady(true);
      });

      // Notify parent on user-initiated pans so it can clear stale banners.
      map.on("dragstart", () => {
        onUserInteractionRef.current?.();
      });
    });

    ro.observe(container);

    return () => {
      ro.disconnect();
      popupRef.current?.remove();
      popupRef.current = null;
      map?.remove();
      mapRef.current = null;
      syncExternalRef(externalRef, null);
      setMapReady(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — externalRef intentionally excluded (stable ref object)

  // Keep the openPopup function up to date when map readiness changes
  useEffect(() => {
    if (!mapReady) {
      openPopupFnRef.current = null;
      return;
    }
    openPopupFnRef.current = (items: DiscoveryItem[]) => {
      const primary = items[0];
      const map = mapRef.current;
      if (
        !(map && primary) ||
        primary.data.latitude == null ||
        primary.data.longitude == null
      ) {
        return;
      }

      // Mobile: skip the Mapbox popup entirely and show a bottom sheet.
      if (isMobile) {
        setSheetItems(items);
        return;
      }

      const lngLat: [number, number] = [
        primary.data.longitude,
        primary.data.latitude,
      ];

      // Increment generation so the previous popup's close listener becomes stale
      popupGenRef.current += 1;
      const gen = popupGenRef.current;

      popupRef.current?.remove();
      const mountEl = document.createElement("div");
      const popup = new mapboxgl.Popup({
        className: "pin-popup",
        closeButton: false,
        closeOnClick: true,
        maxWidth: "none",
        offset: [0, -44],
      })
        .setLngLat(lngLat)
        .setDOMContent(mountEl)
        .addTo(map);

      // Only clear React state if this is still the active popup generation
      popup.on("close", () => {
        if (popupGenRef.current === gen) {
          setPopupMount(null);
        }
      });

      popupRef.current = popup;
      setPopupMount({ el: mountEl, items });

      // Pan to ensure the popup fits fully above the pin without moving the map
      // more than necessary (no-op if pin already has enough clearance).
      panToShowPopup(map, lngLat, { popupHeight: POPUP_HEIGHT });
    };
  }, [mapReady, isMobile]);

  // Sync the MapHandle external ref when map readiness changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: externalRef is a stable ref object — intentionally excluded
  useEffect(() => {
    if (!mapReady) {
      syncExternalRef(externalRef, null);
      return;
    }
    syncExternalRef(externalRef, {
      flyTo(lngLat, zoom = 14, onEnd?: () => void) {
        const map = mapRef.current;
        if (!map) {
          return;
        }
        // Increment generation; onEnd only fires if the animation is not
        // interrupted by the user panning before moveend fires.
        flyToGenRef.current += 1;
        const gen = flyToGenRef.current;
        map.flyTo({ center: lngLat, zoom });
        if (onEnd) {
          map.once("moveend", () => {
            if (flyToGenRef.current === gen) {
              onEnd();
            }
          });
        }
      },
      fitBounds([west, south, east, north], onEnd) {
        const map = mapRef.current;
        if (!map) {
          return;
        }
        flyToGenRef.current += 1;
        const gen = flyToGenRef.current;
        map.fitBounds(
          [
            [west, south],
            [east, north],
          ],
          { padding: 60, duration: 900, easing: easeInOutQuad }
        );
        if (onEnd) {
          map.once("moveend", () => {
            if (flyToGenRef.current === gen) {
              onEnd();
            }
          });
        }
      },
      openPopup(item) {
        openPopupFnRef.current?.([item]);
      },
      getBounds() {
        return mapRef.current?.getBounds() ?? undefined;
      },
      getCenter() {
        const c = mapRef.current?.getCenter();
        return c ? [c.lng, c.lat] : undefined;
      },
      zoomIn() {
        mapRef.current?.zoomIn();
      },
      zoomOut() {
        mapRef.current?.zoomOut();
      },
    });
    return () => {
      syncExternalRef(externalRef, null);
    };
  }, [mapReady]); // eslint-disable-line react-hooks/exhaustive-deps — externalRef intentionally excluded

  // Sync markers whenever geoItems or map readiness changes
  useEffect(() => {
    const map = mapRef.current;
    if (!(map && mapReady)) {
      return;
    }

    for (const m of markersRef.current) {
      m.remove();
    }
    markersRef.current = [];

    if (geoItems.length === 0) {
      return;
    }

    // Fit all pins on initial load only — after the first fit we leave the
    // viewport alone so filter-pill changes don't yank the map away from where
    // the user is looking.
    if (!hasInitialFitRef.current) {
      hasInitialFitRef.current = true;
      flyToBounds(
        map,
        geoItems.map((i) => ({
          latitude: i.data.latitude as number,
          longitude: i.data.longitude as number,
        }))
      );
    }

    // Group co-located items when no filter is active so they share a split pin.
    // When a filter is active each item renders its own pin.
    interface PinGroup {
      items: DiscoveryItem[];
      lngLat: [number, number];
    }
    let pinGroups: PinGroup[];

    if (filtersActive) {
      pinGroups = geoItems.map((item) => ({
        lngLat: [item.data.longitude as number, item.data.latitude as number],
        items: [item],
      }));
    } else {
      const byLocation = new Map<string, DiscoveryItem[]>();
      for (const item of geoItems) {
        const key = locationKey(item);
        const existing = byLocation.get(key);
        if (existing) {
          existing.push(item);
        } else {
          byLocation.set(key, [item]);
        }
      }
      pinGroups = [...byLocation.values()].flatMap((groupItems) => {
        const first = groupItems[0];
        if (!first) {
          return [];
        }
        return [
          {
            lngLat: [
              first.data.longitude as number,
              first.data.latitude as number,
            ] as [number, number],
            items: groupItems,
          },
        ];
      });
    }

    pinGroups.forEach((group, uid) => {
      const primary = group.items[0];
      if (!primary) {
        return;
      }
      const { lngLat } = group;

      // Deduplicate colours (e.g. two ERS at same address → single colour pin)
      const colors = [...new Set(group.items.map(pinColor))];
      const [primaryColor, ...rest] = colors;

      let ariaLabel: string;
      if (group.items.length > 1) {
        ariaLabel = `${group.items.length} groups/programmes at this location`;
      } else if (primary.kind === "group") {
        ariaLabel = `${primary.data.name}, recovery group`;
      } else {
        ariaLabel = `${primary.data.definition?.name ?? "Programme"} in ${primary.data.city ?? "unknown location"}`;
      }

      const btn = markerPin(
        ariaLabel,
        primaryColor ?? "",
        uid,
        rest.length ? rest : undefined
      );

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        openPopupFnRef.current?.(group.items);
      });

      markersRef.current.push(
        new mapboxgl.Marker({ element: btn, anchor: "bottom" })
          .setLngLat(lngLat)
          .addTo(map)
      );
    });
  }, [mapReady, geoItems, filtersActive]);

  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0" ref={containerRef} />
      {popupMount &&
        createPortal(
          <PinDetailCard
            items={popupMount.items}
            onClose={() => popupRef.current?.remove()}
          />,
          popupMount.el
        )}
      <PinDetailSheet
        items={sheetItems ?? []}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSheetItems(null);
          }
        }}
        open={sheetItems !== null}
      />
    </div>
  );
}
